// ENT-3137 (Codex P2 on #2456): the WP1 hardening hook strips the bearer token
// from the address bar so it cannot leak to Amplitude autocapture, the `Referer`
// header, or Sentry. But the token then lived ONLY in React Router's in-memory
// location, so a hard refresh, tab restore, or a copied (already-scrubbed) URL
// remounted `/telelens/supplier` or `/telelens/briefing` with no token and
// reported a still-valid secure link as unavailable.
//
// This module retains the token in a per-tab, session-scoped store
// (`sessionStorage`) keyed by route, so a reload within the same tab recovers
// it. `sessionStorage` is never sent to the server, a referrer, or analytics and
// is cleared when the tab closes, so this preserves every egress surface WP1
// closed while restoring reload resilience. It is the "secure session-scoped
// mechanism" the reviewer asked for.
import { ADVISORY_CTA_TOKEN_KEY, isAdvisoryCtaPath, SENSITIVE_QUERY_KEYS, redactSensitiveUrlParams } from '@/lib/sensitiveUrl';
import { isPublicTokenRoute } from '@/lib/publicTokenRoutes';

const STORAGE_PREFIX = 'telelens:public-token';
// Advisory visit tokens grant no access. Retain them in this page lifetime even when storage
// is blocked so the pre-init scrub never exposes them to instrumentation. A cleared marker
// prevents an inaccessible/stale storage value from being replayed in the current lifetime.
const advisoryTokens = new Map<string, string>();

function normalizePath(pathname: string): string {
  if (typeof pathname !== 'string' || pathname.length === 0) {
    return '';
  }
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed.toLowerCase();
}

function storageKey(pathname: string, key: string): string {
  return `${STORAGE_PREFIX}:${normalizePath(pathname)}:${key}`;
}

function safeSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    return window.sessionStorage;
  } catch {
    // Access itself can throw in sandboxed / storage-blocked contexts.
    return null;
  }
}

function livePathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

/**
 * Persist any sensitive token params present in `search` to per-tab session
 * storage, scoped by route. Only writes when a value is present — it never
 * clears — so a reload (which arrives with an already-scrubbed URL) does not
 * wipe the token stashed on the first visit. Safe to call unconditionally.
 *
 * Returns keys retained in session storage (or memory for advisory tracking only). The
 * pre-init scrubber (ENT-4086 R15-A) uses this so it only removes a token from
 * the URL once it has been retained: in a storage-disabled / sandboxed browser
 * `setItem` throws, nothing is stored, and the token must stay in the URL — the
 * page reads `searchParams.get('token_hash')` — or the invite flow can never
 * verify (there is no other copy of the single-use token). Advisory visit tracking grants no
 * access: its memory copy is sufficient for the current page; storage-blocked reloads may lose
 * an unrecorded visit, without affecting page access.
 */
export function stashPublicTokens(pathname: string, search: string): readonly string[] {
  const store = safeSessionStorage();
  if (typeof search !== 'string' || search.length === 0) {
    return [];
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const persisted: string[] = [];
  const advisory = isAdvisoryCtaPath(pathname);
  // ENT-6463: /telelens/join lands on an invitation or on a verify link, and the page prefers
  // verify. The latest landing decides, so an unconfirmed verify link cannot replace a new invitation.
  if (normalizePath(pathname) === '/telelens/join') {
    const landed = (['token', 'verify'] as const).filter((key) => params.get(key)?.trim());
    if (landed.length === 1 && store) {
      try {
        store.removeItem(storageKey(pathname, landed[0] === 'token' ? 'verify' : 'token'));
      } catch {
        // Blocked storage holds nothing to replay.
      }
    }
  }
  const keys = advisory ? [ADVISORY_CTA_TOKEN_KEY] : SENSITIVE_QUERY_KEYS;
  for (const key of keys) {
    const value = params.get(key)?.trim();
    if (value) {
      if (advisory) {
        advisoryTokens.set(storageKey(pathname, key), value);
        persisted.push(key);
      }
      if (!store) continue;
      try {
        store.setItem(storageKey(pathname, key), value);
        if (!advisory) persisted.push(key);
      } catch {
        // Auth tokens must remain in the URL; advisory tracking is retained in memory.
      }
    }
  }
  return persisted;
}

/**
 * ENT-3137 (Codex round-14 on #2456): synchronous pre-init hardening.
 *
 * `initSentry()` runs in `main.tsx` BEFORE React (and therefore
 * `usePublicTokenUrlHardening`) mounts. Sentry's Session Replay integration
 * records `window.location` at init time, and its recording envelopes are NOT
 * passed through `beforeSend`/`beforeSendTransaction` — so a sampled or
 * error-triggered replay could capture the bearer `?token=...` before the hook
 * scrubs it. This runs the same stash-then-scrub as the hook, but synchronously
 * and gated to the canonical public-token routes, so neither Sentry Replay nor
 * any Sentry event ever observes the token in the address bar.
 *
 * The token is retained in per-tab session storage first, so the TeleLens
 * supplier/briefing pages still recover it via `recallPublicToken` (they read
 * `searchParams.get('token') || recallPublicToken('token')`, not the URL alone).
 * Non-token routes are untouched (their `verify` links may lack the fallback),
 * and all non-sensitive params (`preview`, `campaignId`, `payment`, `action`)
 * are preserved for React Router. Call this BEFORE `initSentry()`.
 */
export function scrubPublicTokenUrlBeforeInit(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) {
    return;
  }

  const { pathname, search, hash } = window.location;
  if (!search || !(isPublicTokenRoute(pathname) || isAdvisoryCtaPath(pathname))) {
    return;
  }

  // Retain the token per-tab BEFORE scrubbing so reload / recall still works.
  // R15-A: only scrub the keys that were ACTUALLY persisted — in a
  // storage-disabled browser nothing is retained, so removing the token from
  // the URL would strand the invite flow (no other copy of the single-use
  // token exists). Leaving it in the URL is the correct trade-off there.
  const persisted = stashPublicTokens(pathname, search);
  if (persisted.length === 0) {
    return;
  }

  const cleanedSearch = redactSensitiveUrlParams(search, persisted);
  if (cleanedSearch === search) {
    return;
  }

  window.history.replaceState(window.history.state, '', `${pathname}${cleanedSearch}${hash}`);
}

/**
 * Recall a previously stashed token for the current route, or `''` when none.
 * Defaults to the live pathname so public-page callers pass only the param key.
 */
export function recallPublicToken(key: string, pathname: string = livePathname()): string {
  if (key === ADVISORY_CTA_TOKEN_KEY && isAdvisoryCtaPath(pathname) && advisoryTokens.has(storageKey(pathname, key))) {
    return advisoryTokens.get(storageKey(pathname, key)) ?? '';
  }
  const store = safeSessionStorage();
  if (!store) {
    return '';
  }
  try {
    return store.getItem(storageKey(pathname, key))?.trim() ?? '';
  } catch {
    return '';
  }
}

/**
 * ENT-6463: stash a token the page received in a response rather than the URL, under the
 * route that will recall it. Returns false when session storage is unavailable, so the
 * caller can carry the token in the URL instead (the destination strips it on load).
 */
export function rememberPublicToken(key: string, value: string, pathname: string): boolean {
  const store = safeSessionStorage();
  if (!store) {
    return false;
  }
  try {
    store.setItem(storageKey(pathname, key), value);
    return true;
  } catch {
    return false;
  }
}

/**
 * ENT-4086 (Codex R7): remove a stashed token for the current route once it has
 * been consumed. Single-use tokens (the GoTrue invite/magic-link `token_hash`)
 * must be cleared after a successful `verifyOtp`, otherwise a reload recalls the
 * already-consumed value, re-enters the verification branch, and shows the
 * invalid-link screen instead of the marker-based reload path. Defaults to the
 * live pathname so page callers pass only the param key.
 */
export function clearPublicToken(key: string, pathname: string = livePathname()): void {
  if (key === ADVISORY_CTA_TOKEN_KEY && isAdvisoryCtaPath(pathname)) {
    advisoryTokens.set(storageKey(pathname, key), '');
  }
  const store = safeSessionStorage();
  if (!store) {
    return;
  }
  try {
    store.removeItem(storageKey(pathname, key));
  } catch {
    // Blocked storage — nothing to clear; recall already degrades to '' there.
  }
}
